/* eslint-disable react-refresh/only-export-components */
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import type { DocumentRow } from "@/hooks/use-documents";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#1a1a1a",
    backgroundColor: "#fff",
  },
  header: {
    marginBottom: 24,
    borderBottom: "1px solid #e0e0e0",
    paddingBottom: 12,
  },
  kind: {
    fontSize: 8,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: "#666",
    marginBottom: 4,
  },
  title: {
    fontSize: 22,
    fontFamily: "Helvetica-Bold",
    color: "#1a1a1a",
  },
  section: {
    marginBottom: 16,
  },
  row: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  label: {
    width: 130,
    fontSize: 8,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#888",
    paddingTop: 1,
  },
  value: {
    flex: 1,
    fontSize: 10,
    color: "#1a1a1a",
  },
  valueBold: {
    flex: 1,
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: "#1a1a1a",
  },
  divider: {
    borderBottom: "1px solid #f0f0f0",
    marginVertical: 10,
  },
  qrNote: {
    marginTop: 20,
    padding: 10,
    backgroundColor: "#f5f5f5",
    fontSize: 8,
    color: "#666",
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 7,
    color: "#bbb",
    textAlign: "center",
  },
});

function FieldRow({
  label,
  value,
  bold,
}: {
  label: string;
  value: string | null | undefined;
  bold?: boolean;
}) {
  if (!value) return null;
  return (
    <View style={styles.row}>
      <Text style={styles.label}>{label}</Text>
      <Text style={bold ? styles.valueBold : styles.value}>{value}</Text>
    </View>
  );
}

function Header({ doc }: { doc: DocumentRow }) {
  return (
    <View style={styles.header}>
      <Text style={styles.kind}>{doc.kind.replace(/_/g, " ")}</Text>
      <Text style={styles.title}>{doc.title}</Text>
    </View>
  );
}

function Footer() {
  return (
    <Text style={styles.footer} fixed>
      Generated by Haus
    </Text>
  );
}

function QrNote({ pnr }: { pnr?: string }) {
  if (!pnr) return null;
  return (
    <View style={styles.qrNote}>
      <Text>PNR / Booking ref: {pnr}</Text>
      <Text style={{ marginTop: 4 }}>Scan the QR code from your original confirmation email at check-in.</Text>
    </View>
  );
}

function FlightTicketDoc({ doc }: { doc: DocumentRow }) {
  const m = (doc.metadata ?? {}) as Record<string, string>;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header doc={doc} />
        <View style={styles.section}>
          <FieldRow label="Airline" value={m.airline} bold />
          <FieldRow label="Flight" value={m.flight_number} bold />
          <View style={styles.divider} />
          <FieldRow label="From" value={m.origin} />
          <FieldRow label="To" value={m.destination} />
          <FieldRow label="Departure" value={m.departure_at} />
          <FieldRow label="Arrival" value={m.arrival_at} />
          <View style={styles.divider} />
          <FieldRow label="Seat" value={m.seat} />
          <FieldRow label="Class" value={m.cabin_class} />
          <FieldRow label="Booking ref (PNR)" value={m.pnr} bold />
          <FieldRow label="Passenger" value={m.passenger_name} />
          <FieldRow label="Terminal" value={m.terminal} />
        </View>
        {doc.notes ? (
          <View style={styles.section}>
            <FieldRow label="Notes" value={doc.notes} />
          </View>
        ) : null}
        <QrNote pnr={m.pnr} />
        <Footer />
      </Page>
    </Document>
  );
}

function TrainTicketDoc({ doc }: { doc: DocumentRow }) {
  const m = (doc.metadata ?? {}) as Record<string, string>;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header doc={doc} />
        <View style={styles.section}>
          <FieldRow label="Operator" value={m.operator ?? m.airline} />
          <FieldRow label="Train" value={m.train_number ?? m.flight_number} bold />
          <View style={styles.divider} />
          <FieldRow label="From" value={m.origin} />
          <FieldRow label="To" value={m.destination} />
          <FieldRow label="Departure" value={m.departure_at} />
          <FieldRow label="Arrival" value={m.arrival_at} />
          <View style={styles.divider} />
          <FieldRow label="Coach / Seat" value={m.seat} />
          <FieldRow label="Class" value={m.cabin_class} />
          <FieldRow label="Booking ref" value={m.pnr} bold />
          <FieldRow label="Passenger" value={m.passenger_name} />
        </View>
        {doc.notes ? (
          <View style={styles.section}>
            <FieldRow label="Notes" value={doc.notes} />
          </View>
        ) : null}
        <QrNote pnr={m.pnr} />
        <Footer />
      </Page>
    </Document>
  );
}

function HotelBookingDoc({ doc }: { doc: DocumentRow }) {
  const m = (doc.metadata ?? {}) as Record<string, string>;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header doc={doc} />
        <View style={styles.section}>
          <FieldRow label="Hotel" value={m.hotel_name} bold />
          <FieldRow label="Address" value={m.address} />
          <View style={styles.divider} />
          <FieldRow label="Check-in" value={m.checkin_at} />
          <FieldRow label="Check-out" value={m.checkout_at} />
          <FieldRow label="Nights" value={m.nights} />
          <View style={styles.divider} />
          <FieldRow label="Confirmation" value={m.confirmation_number ?? m.pnr} bold />
          <FieldRow label="Room type" value={m.room_type} />
          <FieldRow label="Guests" value={m.guests} />
          <FieldRow label="Rate" value={m.rate} />
        </View>
        {doc.notes ? (
          <View style={styles.section}>
            <FieldRow label="Notes" value={doc.notes} />
          </View>
        ) : null}
        <QrNote pnr={m.confirmation_number ?? m.pnr} />
        <Footer />
      </Page>
    </Document>
  );
}

function IdentityDoc({ doc }: { doc: DocumentRow }) {
  const m = (doc.metadata ?? {}) as Record<string, string>;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header doc={doc} />
        <View style={styles.section}>
          <FieldRow label="Issuer" value={doc.issuer} />
          <FieldRow label="Country" value={doc.issued_country} />
          <FieldRow label="Issued" value={doc.issued_at} />
          <FieldRow label="Expires" value={doc.expires_at} />
          {Object.entries(m).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
          ))}
        </View>
        <View style={styles.qrNote}>
          <Text>Document number is stored encrypted. Reveal it in the Haus app.</Text>
        </View>
        <Footer />
      </Page>
    </Document>
  );
}

function GenericDoc({ doc }: { doc: DocumentRow }) {
  const m = (doc.metadata ?? {}) as Record<string, string>;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header doc={doc} />
        <View style={styles.section}>
          <FieldRow label="Issuer" value={doc.issuer} />
          <FieldRow label="Country" value={doc.issued_country} />
          <FieldRow label="Issued" value={doc.issued_at} />
          <FieldRow label="Expires" value={doc.expires_at} />
          {Object.entries(m).map(([k, v]) => (
            <FieldRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
          ))}
        </View>
        {doc.notes ? (
          <View style={styles.section}>
            <FieldRow label="Notes" value={doc.notes} />
          </View>
        ) : null}
        <Footer />
      </Page>
    </Document>
  );
}

function docForKind(doc: DocumentRow) {
  const k = doc.kind.toLowerCase();
  if (k === "flight_ticket" || k === "flight") return <FlightTicketDoc doc={doc} />;
  if (k === "train_ticket" || k === "train") return <TrainTicketDoc doc={doc} />;
  if (k === "hotel_booking" || k === "hotel") return <HotelBookingDoc doc={doc} />;
  if (k === "passport" || k === "drivers_license" || k === "national_id") return <IdentityDoc doc={doc} />;
  return <GenericDoc doc={doc} />;
}

export async function downloadDocumentPdf(doc: DocumentRow): Promise<void> {
  const element = docForKind(doc);
  const blob = await pdf(element).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${doc.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
